import { cn } from '@/utils/cn'

type RiskTier = 'low' | 'medium' | 'high' | 'critical'

interface AIScoreCardProps {
  score: number
  riskTier: RiskTier
  confidenceInterval: { lower: number; upper: number }
  featureImportance: Record<string, number>
  explanation: string
  isDemo?: boolean
  className?: string
}

const RISK_TIER_COLORS: Record<RiskTier, { bg: string; text: string; border: string }> = {
  low: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', border: 'border-green-200 dark:border-green-800' },
  medium: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', border: 'border-yellow-200 dark:border-yellow-800' },
  high: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-300', border: 'border-orange-200 dark:border-orange-800' },
  critical: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', border: 'border-red-200 dark:border-red-800' },
}

const GAUGE_COLORS: Record<RiskTier, string> = {
  low: '#10b981',
  medium: '#f59e0b',
  high: '#f97316',
  critical: '#ef4444',
}

export function AIScoreCard({
  score,
  riskTier,
  confidenceInterval,
  featureImportance,
  explanation,
  isDemo = false,
  className,
}: AIScoreCardProps) {
  const tierColors = RISK_TIER_COLORS[riskTier]
  const gaugeColor = GAUGE_COLORS[riskTier]

  // Score gauge calculations
  const size = 140
  const thickness = 12
  const radius = (size - thickness) / 2
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (score / 100) * circumference

  // Sort features by importance descending
  const sortedFeatures = Object.entries(featureImportance).sort(([, a], [, b]) => b - a)
  const maxImportance = sortedFeatures.length > 0 ? Math.max(...sortedFeatures.map(([, v]) => v)) : 1

  return (
    <div
      className={cn(
        'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6',
        className
      )}
      aria-label="AI Score Card"
    >
      {/* Header with Demo badge */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          AI Health Score
        </h3>
        {isDemo && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
            Demo
          </span>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Score Gauge */}
        <div className="flex-shrink-0 flex flex-col items-center">
          <div className="relative inline-flex items-center justify-center">
            <svg
              width={size}
              height={size}
              className="transform -rotate-90"
              aria-hidden="true"
            >
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke="currentColor"
                strokeWidth={thickness}
                fill="none"
                className="text-gray-200 dark:text-gray-700"
              />
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke={gaugeColor}
                strokeWidth={thickness}
                fill="none"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                className="transition-all duration-1000 ease-out"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span
                className="text-3xl font-bold text-gray-900 dark:text-white"
                aria-label={`Score: ${score} out of 100`}
              >
                {score.toFixed(0)}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">/ 100</span>
            </div>
          </div>

          {/* Risk Tier Badge */}
          <span
            className={cn(
              'mt-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border',
              tierColors.bg,
              tierColors.text,
              tierColors.border
            )}
            aria-label={`Risk tier: ${riskTier}`}
          >
            {riskTier.charAt(0).toUpperCase() + riskTier.slice(1)} Risk
          </span>

          {/* Confidence Interval */}
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
            Confidence: {confidenceInterval.lower.toFixed(1)} – {confidenceInterval.upper.toFixed(1)}
          </p>
        </div>

        {/* Right side: Feature Importance + Explanation */}
        <div className="flex-1 min-w-0">
          {/* Feature Importance Breakdown */}
          {sortedFeatures.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Feature Importance
              </h4>
              <div className="space-y-2" role="list" aria-label="Feature importance breakdown">
                {sortedFeatures.map(([feature, value]) => (
                  <div key={feature} className="flex items-center gap-2" role="listitem">
                    <span className="text-xs text-gray-600 dark:text-gray-400 w-28 truncate" title={feature}>
                      {feature}
                    </span>
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-500"
                        style={{
                          width: `${(value / maxImportance) * 100}%`,
                          backgroundColor: gaugeColor,
                        }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 w-10 text-right">
                      {(value * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Explanation */}
          {explanation && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Explanation
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {explanation}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
